/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  TreeSelect,
  Alert,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';

// APIs del recurso hijo
import {
  getPercepcionParametroDiversoCuentasContablesArea,
  postPercepcionParametroDiversoCuentaContableArea,
  patchPercepcionParametroDiversoCuentaContableArea,
  deletePercepcionParametroDiversoCuentaContableArea,
  getPercepcionParametroDiversoCuentaContableArea,
} from 'api/nominas/percepciones/percepcionParametroDiversoCuentaContableArea';
import makeTreeFromCuentasContables from 'utils/makeTreeFromCuentasContables';
import { getAreas, getCuentasContables } from 'api/catalogos';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

const AreasContablesTabla = ({
  parametroDiversoId,
  disabled = false,
  permission,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [cuentasContables, setCuentasContables] = useState([]);
  const [areas, setAreas] = useState([]);

  const normalizeCuentasContables = (_data) => {
    if (_data) {
      return _data.map((item) => {
        const clone = { ...item };
        const children = normalizeCuentasContables(clone.children);

        return {
          ...clone,
          title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
          value: clone.id,
          key: clone.id,
          children: children.length ? children : undefined,
          disabled: false, // <-- Quitamos la restricción, todas son seleccionables
        };
      });
    }
    return [];
  };

  const normalizeDataAreas = (_areasFetchArray) => {
    const mainMap = new Map();
    _areasFetchArray.forEach((item) => {
      mainMap.set(item.id, { ...item, children: [] });
    });
    const result = [];
    _areasFetchArray.forEach((item) => {
      if (item.directorio_area_padre) {
        const parent = mainMap.get(item.directorio_area_padre);
        if (parent) parent.children.push(mainMap.get(item.id));
      } else {
        result.push(mainMap.get(item.id));
      }
    });
    const sortByClave = (nodes) => {
      nodes.sort((a, b) => a.clave.localeCompare(b.clave));
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) sortByClave(node.children);
      });
    };
    sortByClave(result);
    return result;
  };

  const formatTreeSelectData = (nodes) => nodes.map((node) => ({
    ...node,
    title: `${node.clave} - ${node.nombre.trim()}`,
    value: node.id,
    key: node.id,
    children: node.children && node.children.length
      ? formatTreeSelectData(node.children)
      : undefined,
  }));

  const formInstances = [form];

  // Función recursiva para buscar nodos en CUALQUIER árbol (Cuentas o Áreas)
  const findInTree = (nodes, id) => {
    if (!Array.isArray(nodes)) return null;
    return nodes.reduce((found, node) => {
      if (found) return found;
      if (node.id === id) return node;
      if (node.children) return findInTree(node.children, id) || found;
      return found;
    }, null);
  };

  const fetchData = async () => {
    if (!parametroDiversoId) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const _data = await getPercepcionParametroDiversoCuentasContablesArea({
        parametro_diverso: parametroDiversoId,
      });
      setData(formatReceived(_data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Áreas Contables] error al cargar cuentas contables del área', error);
    } finally {
      setLoading(false);
    }
  };

  // Se vuelve a llamar la información si cambia el ID del padre
  useEffect(() => {
    let mounted = true;

    const fetchAllData = async () => {
      // Si no hay ID del padre, no cargamos nada
      if (!parametroDiversoId) {
        setData([]);
        return;
      }

      setLoading(true);
      try {
        // 1. Disparamos todas las peticiones al mismo tiempo
        const [
          _cuentasContables,
          _areas,
          _tablaData, // Lo que antes hacía tu fetchData()
        ] = await Promise.all([
          getCuentasContables(),
          getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
          // eslint-disable-next-line max-len
          getPercepcionParametroDiversoCuentasContablesArea({ parametro_diverso: parametroDiversoId }),
        ]);
        if (mounted) {
          const arbolCuentas = makeTreeFromCuentasContables(_cuentasContables);
          const cuentasNormalizadas = normalizeCuentasContables(arbolCuentas);
          setCuentasContables(cuentasNormalizadas);
          setAreas(formatTreeSelectData(normalizeDataAreas(_areas)));
          setData(formatReceived(_tablaData));
          setSelectedRowKeys([]);
          setSelectedData({});
        }
      } catch (error) {
        message.error('Error al cargar la información de áreas y cuentas');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAllData();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [parametroDiversoId]);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);

    if (match) {
      const _item = await getPercepcionParametroDiversoCuentaContableArea(match.id);
      setTimeout(() => {
        if (_item) {
          form.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(_item));
            setVisible(true);
          });
        }
      });
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      // Asociar obligatoriamente al parámetro diverso actual
      values.parametro_diverso = parametroDiversoId;

      const [key] = selectedRowKeys;

      const saved = await (key
        ? patchPercepcionParametroDiversoCuentaContableArea(key, values)
        : postPercepcionParametroDiversoCuentaContableArea(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionParametroDiversoCuentaContableArea(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    if (!parametroDiversoId) {
      message.warning('Debe guardar el Parámetro Diverso padre antes de agregar áreas.');
      return;
    }
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Directorio de Área',
      dataIndex: 'directorio_de_area',
      key: 'directorio_de_area',
      width: 300,
      render: (val) => {
        const match = findInTree(areas, val);
        return match ? match.title : val;
      },
    },
    {
      titleText: 'Cuenta Contable',
      dataIndex: 'cuenta_contable',
      key: 'cuenta_contable',
      width: 400,
      render: (val) => {
        const _cuenta = findInTree(cuentasContables, val);
        return _cuenta ? `${_cuenta.clave_visible || _cuenta.clave} - ${_cuenta.nombre}` : val;
      },
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  return (
    <Row className="container" style={{ marginTop: 15 }}>
      <Col span={24}>
        {!parametroDiversoId && (
          <Alert
            message="Atención"
            description="Debe guardar el Parámetro Diverso actual para poder agregar y gestionar cuentas contables por área."
            type="warning"
            showIcon
            style={{ marginBottom: 15 }}
          />
        )}
        <Spin tip="Cargando..." spinning={loading}>
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            allowExportPDF={false}
            permission={{
              hasAllViewPermission: true,
              add: true,
              delete: true,
            }}
            hasAllViewPermission
          />

          <Modal
            open={open}
            closable={false}
            maskClosable={!loading}
            keyboard={!loading}
            footer={null}
            title={(
              <FormSubmitControls
                label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cuenta Contable por Área`}
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                allowSave={!disabled}
              />
            )}
            onCancel={onCancel}
            forceRender
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="areas_contables_tabla_form"
            >
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="directorio_de_area"
                  label="Directorio de Área"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    treeData={areas}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="cuenta_contable"
                  label="Cuenta Contable del Gasto"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    treeData={cuentasContables}
                   // disabled={disabled}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  />
                </Form.Item>
              </Col>

              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Modal>

          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            open={visibleAlert}
            content="la cuenta contable por área seleccionada"
            loading={loading}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default AreasContablesTabla;
